import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/shared/apiService';
import { TherapistApplication } from '../shared/types/src';
import PageContainer from '../../components/common/PageContainer';
import PageSection from '../../components/common/PageSection';
import { useTheme } from '../../context/shared/ThemeContext';

const TherapistApproval: React.FC = () => {
  const { themeClasses } = useTheme();
  const [applications, setApplications] = useState<TherapistApplication[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getPendingApplications();
      setApplications(data || []);
    } catch (err: any) {
      console.error('Error fetching applications:', err);
      setError(err.message || 'Failed to fetch applications.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleUpdateStatus = async (userId: string, status: 'approved' | 'denied') => {
    try {
        if (status === 'approved') {
            await apiService.approveTherapist(userId);
          } else {
            // TODO: Implement deny functionality when backend endpoint is available
            console.log(`Deny action for ${userId} is not yet implemented.`);
        }
        fetchApplications(); // Refresh the list after update
        // In a real app, show a success toast
    } catch (err: any) {
        console.error(`Failed to ${status} application:`, err);
        // In a real app, show an error toast
    }
  };

  return (
    <PageContainer>
      <PageSection title="Therapist Approvals" emoji="📋">
        {isLoading ? (
          <p>Loading applications...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${themeClasses.border}`}>
              <thead className={themeClasses.secondaryCardBg}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${themeClasses.secondaryText}`}>Name</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${themeClasses.secondaryText}`}>Email</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${themeClasses.secondaryText}`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${themeClasses.secondaryText}`}>Submitted</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className={`${themeClasses.cardBg} divide-y ${themeClasses.border}`}>
                {applications.map((app) => (
                  <tr key={app.userId}>
                    <td className="px-6 py-4 whitespace-nowrap">{app.fullName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{app.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        {app.applicationStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{new Date(app.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button onClick={() => handleUpdateStatus(app.userId, 'approved')} className="text-green-600 hover:text-green-900 mr-4">Approve</button>
                      <button onClick={() => handleUpdateStatus(app.userId, 'denied')} className="text-red-600 hover:text-red-900">Deny</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {applications.length === 0 && <p className="text-center py-4">No pending applications.</p>}
          </div>
        )}
      </PageSection>
    </PageContainer>
  );
};

export default TherapistApproval; 